import { JourneySteps, JourneyType } from "./journey";
import { SvgIconComponent } from "@mui/icons-material";
import { IconTypes } from "../../components/icons.types";

export const featureKeys = [
  "shopify",
  "woocommerce",
  "csv",
  "pdfImport",
  "productUpload",
  "catalogs",
  "products",
  "categories",
  "collections",
  "images",
  "orders",
  "analytics",
  "inventory",
  "account",
  "billing",
  "upgrade_plan",
  "viewUsage",
  "scheduleDemo",
  "account_profile",
  "account_security",
  "account_business",
  "account_admin",
  "account_users",
  "account_log",
  "catalogTemplates",
  "autoGrid",
  "productList",
  "productCard",
  "card",
  "layout",
  "design_layout",
  "catalogBuilder",
  "openCatalogDesigner",
  "catalog_preview",
  "catalog_publish",
  "catalog_download",
  "catalog_share",
  "newCatalog",
  "parameters",
  "help",
  // Showroom features
  "showroom",
  "showroomPages",
  "showroomProducts",
  "showroomDisplay",
  "showroomSharing",
  // Order management features
  "orderSettings",
  "orderForm",
  "orderStatus",
  "orderExport",
  "paymentSettings",
  // Inventory management features
  "inventoryEdit",
  "inventoryBulk",
  "inventoryFilters",
  "inventoryExport",
  // Advanced features
  "productCatalogMapping",
  // Automation features
  "automation",
  // AI features
  "copilot",
  "support",
  "concierge",
] as const;

// Create type from the keys
export type FeatureKey = (typeof featureKeys)[number];

// Base feature interface
export interface Feature<T extends JourneyType = JourneyType> {
  key: string;
  aliases?: string[]; // Alternative keys (e.g., snake_case variants for backend compatibility)
  title: string;
  description: string;
  image?: string;
  icon?: SvgIconComponent;
  path?: string;
  href?: string;
  helpUrl?: string;
  requiresCatalog?: boolean; // Action requires catalog context (navigates to catalog with ?start=X)
  requiresProducts?: boolean; // Action requires products (opens product modal)
  requiresStore?: boolean; // Action requires showroom (navigates to showroom)
  startMode?: string; // Query param value for ?start= (catalog designer modes, showroom tabs, order tabs, etc.)
  journeyType?: T;
  completionStep?: JourneySteps<T>;
  completionParams?: Record<string, string>;
}

// Type for the features record enforcing all keys are present
export type Features = {
  [K in FeatureKey]: Feature;
};

// Define features with type safety
export const appFeatures: Features = {
  shopify: {
    key: "shopifyImport",
    aliases: ["import_shopify"], // Backend workflow action alias
    title: "Import from Shopify",
    description: "Import your existing Shopify products in minutes",
    image: "/images/shopify.png",
    icon: IconTypes.Store,
    path: "/import/shopify",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/10432942-importing-products-from-shopify-v2",
    journeyType: "product_setup",
    completionStep: "shopify-import",
    completionParams: { service: "shopify" },
  },
  woocommerce: {
    key: "woocommerce",
    title: "Import from WooCommerce",
    description: "Preview your WooCommerce store and pick the products to import",
    // Square, like the other cards; the wide wordmark is the import page's empty state.
    image: "/images/woocommerce-icon.png",
    icon: IconTypes.Store,
    path: "/import/woocommerce",
  },
  // Showroom features
  showroom: {
    key: "showroom",
    title: "Product Showroom",
    description: "Create a searchable product website",
    image: "/images/showroom.png",
    icon: IconTypes.Store,
    path: "/showrooms",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/11176167-product-showrooms-v2",
    journeyType: "discovery",
    completionStep: "discover_showroom",
  },
  showroomProducts: {
    key: "showroomProducts",
    title: "Showroom Products",
    description: "Manage products in your showroom",
    icon: IconTypes.Product,
    requiresStore: true,
    startMode: "products", // Showroom tab name
    journeyType: "discovery",
    completionStep: "showroom_products",
  },
  showroomPages: {
    key: "showroomPages",
    title: "Showroom Pages",
    description: "Create custom pages for your showroom",
    icon: IconTypes.Pages,
    requiresStore: true,
    startMode: "pages", // Showroom tab name
    journeyType: "discovery",
    completionStep: "showroom_pages",
  },
  showroomDisplay: {
    key: "showroomDisplay",
    title: "Showroom Display",
    description: "Customize your showroom appearance",
    icon: IconTypes.DisplaySettings,
    requiresStore: true,
    startMode: "display", // Showroom tab name
    journeyType: "discovery",
    completionStep: "customize_showroom",
  },
  showroomSharing: {
    key: "showroomSharing",
    title: "Showroom Sharing",
    description: "Set privacy and sharing options",
    icon: IconTypes.Share,
    requiresStore: true,
    startMode: "settings", // Showroom tab name
    journeyType: "discovery",
    completionStep: "showroom_sharing",
  },
  csv: {
    key: "csvImport",
    aliases: ["import_csv"], // Backend workflow action alias
    title: "Import from CSV / Excel",
    description: "Import your products from a spreadsheet",
    image: "/images/excel.png",
    icon: IconTypes.Upload,
    path: "/products?import=csv",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/10451242-importing-products-from-csv-v2",
    journeyType: "product_setup",
    completionStep: "csv-import",
    completionParams: { service: "csv" },
  },
  pdfImport: {
    key: "pdfImport",
    aliases: ["import_pdf"], // Backend workflow action alias
    title: "Import from PDF",
    description: "Import existing PDF pages as images to create new catalogs",
    image: "/images/pdf-import.png",
    icon: IconTypes.Upload,
    path: "/catalogs/pdf-import",
    helpUrl: "https://help.catalogmachine.com/en/articles/pdf-import-guide",
    journeyType: "catalog_creation",
    completionStep: "pdf-import",
    completionParams: { service: "pdf" },
  },
  productUpload: {
    key: "productUpload",
    aliases: ["product_upload"], // Backend workflow action alias
    title: "Product bulk upload",
    description: "Upload multiple products with images at once",
    image: "/images/image-upload.png",
    icon: IconTypes.AddImage,
    path: "/products/upload",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/10458534-managing-products-v2",
    journeyType: "product_setup",
    completionStep: "upload_products",
  },
  catalogs: {
    key: "createCatalog",
    title: "Create Catalogs",
    description: "Create your product catalogs",
    image: "/images/catalogue.png",
    icon: IconTypes.Catalog,
    path: "/catalogs",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9269964-build-your-product-catalog-v2",
    journeyType: "onboarding",
    completionStep: "first_catalog",
  },
  products: {
    key: "addProducts",
    // add_manual / add_products: legacy/AI-emitted snake_case keys for manual product entry
    // (not in the app-map; the AI emits them anyway). Resolve to the products grid instead
    // of a dead "Action not available" snackbar. See copilot batch 8 (la-linda #320930).
    aliases: ["variants", "addProducts", "add_manual", "add_products"],
    title: "Add / edit products",
    description: "Create your products in the grid",
    image: "/images/products.png",
    icon: IconTypes.Add,
    path: "/products",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/10458534-managing-products-v2",
    journeyType: "onboarding",
    completionStep: "first_product",
  },
  orders: {
    key: "orders",
    title: "Orders",
    description: "Manage customer orders",
    icon: IconTypes.Order,
    image: "/images/orders.png",
    path: "/orders",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/11963079-online-orders-payments-v2",
  },
  analytics: {
    key: "analytics",
    title: "Analytics",
    description: "See how people use your catalogs and showroom",
    icon: IconTypes.Analytics,
    path: "/analytics",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/17326760-catalog-showroom-analytics-v2",
  },
  inventory: {
    key: "inventory",
    title: "Inventory",
    description: "Track your inventory levels",
    icon: IconTypes.Inventory,
    image: "/images/inventory.png",
    path: "/products/inventory",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/11973178-inventory-management-v2",
  },
  images: {
    key: "manage_images",
    aliases: ["manage_images"], // Substep uses this key
    title: "Upload / edit Images",
    description: "Manage images",
    image: "/images/image-gallery.png",
    path: "/images",
    icon: IconTypes.Image,
    journeyType: "discovery",
    completionStep: "manage_images",
  },
  account: {
    key: "manageAccount",
    aliases: ["settings"], // Backend workflow action alias
    title: "Manage account",
    description: "Update your account settings",
    image: "/images/user.png",
    path: "/account",
    journeyType: "discovery",
    completionStep: "account_settings",
  },
  account_profile: {
    key: "account_profile",
    title: "Personal Profile",
    description: "Manage user profile: name, email, avatar, notifications",
    icon: IconTypes.Person,
    path: "/account/profile",
    journeyType: "discovery",
  },
  account_security: {
    key: "account_security",
    title: "Security Settings",
    description: "Manage passwords, two-factor auth, social logins",
    icon: IconTypes.Secure,
    path: "/account/security",
    journeyType: "discovery",
  },
  account_business: {
    key: "account_business",
    title: "Business Settings",
    description: "Configure namespace, company name, language, branding",
    icon: IconTypes.Business,
    path: "/account/business",
    journeyType: "discovery",
  },
  account_admin: {
    key: "account_admin",
    title: "Account Admin",
    description: "Wipe data, delete account, Shopify connection",
    icon: IconTypes.Settings,
    path: "/account/admin",
    journeyType: "discovery",
  },
  account_users: {
    key: "account_users",
    title: "Manage Users",
    description: "Add, remove, manage user access and permissions",
    icon: IconTypes.Users,
    path: "/account/users",
    journeyType: "discovery",
  },
  account_log: {
    key: "account_log",
    title: "Access Log",
    description: "View catalog and showroom access logs",
    icon: IconTypes.History,
    path: "/account/log",
    journeyType: "discovery",
  },
  billing: {
    key: "billing",
    title: "Plans & Billing",
    description: "View plans, upgrade subscription, and manage billing",
    icon: IconTypes.Money,
    path: "/account/billing/subscription",
    journeyType: "discovery",
    completionStep: "upgrade_plan",
  },
  upgrade_plan: {
    key: "upgrade_plan",
    title: "Upgrade Plan",
    description: "View pricing plans and upgrade subscription",
    icon: IconTypes.Money,
    path: "/account/billing/plan",
    journeyType: "discovery",
  },
  viewUsage: {
    key: "viewUsage",
    title: "View Usage & Limits",
    description: "Check current usage vs plan limits",
    icon: IconTypes.Dashboard,
    path: "/", // Dashboard shows usage breakdown
    journeyType: "discovery",
  },
  scheduleDemo: {
    key: "scheduleDemo",
    title: "Schedule Support Call",
    description: "Book 1-on-1 video call with support",
    icon: IconTypes.HelpOutline,
    href: "https://api.catalogmachine.com/contact/booking",
    journeyType: "discovery",
  },
  catalogTemplates: {
    key: "catalogTemplates",
    title: "Start with Sample Catalog",
    description:
      "Begin with pre-made sample catalog with products and customize them for your needs",
    image: "/images/product-box.png",
    icon: IconTypes.Templates,
    path: "/catalogs/templates",
  },
  categories: {
    key: "categories",
    title: "Setup categories",
    description: "Manage your product categories",
    icon: IconTypes.Category,
    path: "/products/categories",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/10458534-managing-products-v2",
    journeyType: "product_setup",
    completionStep: "category_setup",
  },
  collections: {
    key: "collections",
    title: "Create Collections",
    description: "Organize your product",
    icon: IconTypes.Collection,
    path: "/products/collections",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/10458534-managing-products-v2",
    journeyType: "product_setup",
    completionStep: "create_collections",
  },
  autoGrid: {
    key: "autoGrid",
    aliases: ["use_auto_grid", "grid"], // Backend uses snake_case + designer start mode
    title: "Auto Grid",
    description: "Quickly add multiple products",
    icon: IconTypes.Grid,
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9289226-build-product-grids-instantly-with-autogrid-v2",
    requiresCatalog: true,
    startMode: "use_auto_grid", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "use_auto_grid",
  },
  productList: {
    key: "productList",
    aliases: ["use_product_list", "list"], // Backend uses snake_case + designer start mode
    title: "Product / Price List",
    description: "Create product price tables",
    icon: IconTypes.ProductList,
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9296058-create-product-and-price-lists-v2",
    requiresCatalog: true,
    startMode: "use_product_list", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "use_product_list",
  },
  card: {
    key: "card",
    title: "Use Product Card",
    description: "Apply a product card design to catalog page",
    icon: IconTypes.ProductCard,
    requiresCatalog: true,
    startMode: "card", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
  },
  productCard: {
    key: "productCard",
    aliases: ["design_card"], // Backend uses snake_case
    title: "Product Cards",
    description: "Define product display styles",
    icon: IconTypes.ProductCard,
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9768970-product-cards-v2",
    path: "/design/cards",
    requiresCatalog: true, // Can work in catalog context
    startMode: "card", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "design_card",
  },
  layout: {
    key: "layout",
    title: "Page Layout",
    description: "Add page template",
    icon: IconTypes.Layout,
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9269964-build-your-product-catalog-v2",
    requiresCatalog: true, // Can work in catalog context
    startMode: "layout", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "use_layout",
  },
  design_layout: {
    key: "design_layout",
    title: "Design Layout",
    description: "Build your page layouts",
    icon: IconTypes.Layout,
    path: "/design/layouts",
    journeyType: "catalog_creation",
    completionStep: "design_layout",
  },
  catalogBuilder: {
    key: "catalogBuilder",
    aliases: ["catalog_builder"], // Backend uses snake_case
    title: "Catalog Builder",
    description: "Build and visualize your catalog structure",
    icon: IconTypes.Dashboard,
    requiresCatalog: true,
    startMode: "builder", // CatalogPages useEffect mode (not CatalogDesigner)
    journeyType: "catalog_creation",
    completionStep: "discover_builder",
  },
  openCatalogDesigner: {
    key: "openCatalogDesigner",
    aliases: ["open_catalog_designer"], // Backend uses snake_case
    title: "Open Catalog Designer",
    description: "Edit catalog pages and content in the designer",
    icon: IconTypes.Design,
    requiresCatalog: true,
    journeyType: "catalog_creation",
  },
  catalog_preview: {
    key: "catalog_preview",
    aliases: ["preview"], // Legacy alias for startMode compatibility
    title: "Preview Catalog",
    description: "Preview catalog before sharing",
    icon: IconTypes.OpenInNew,
    requiresCatalog: true,
    startMode: "preview", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "preview",
  },
  catalog_publish: {
    key: "catalog_publish",
    aliases: ["publish"], // Legacy alias for startMode compatibility
    title: "Publish Catalog",
    description: "Make catalog publicly accessible",
    icon: IconTypes.Publish,
    requiresCatalog: true,
    startMode: "publish", // CatalogDesigner handleStart mode
    journeyType: "onboarding",
    completionStep: "catalog_published",
  },
  catalog_download: {
    key: "catalog_download",
    aliases: ["download"], // Legacy alias for startMode compatibility
    title: "Download PDF",
    description: "Generate and download print-ready PDF",
    icon: IconTypes.PDF,
    requiresCatalog: true,
    startMode: "download", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "download_pdf",
  },
  catalog_share: {
    key: "catalog_share",
    aliases: ["share"], // Legacy alias for startMode compatibility
    title: "Get Sharing Link",
    description: "Get shareable web link for catalog",
    icon: IconTypes.Share,
    requiresCatalog: true,
    startMode: "share", // CatalogDesigner handleStart mode
    journeyType: "catalog_creation",
    completionStep: "share",
  },
  newCatalog: {
    key: "newCatalog",
    aliases: ["new_catalog", "create_catalog"], // Backend workflow action aliases
    title: "Create New Catalog",
    description: "Start a brand new catalog from scratch",
    icon: IconTypes.Add,
    path: "/catalogs/create",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9269964-build-your-product-catalog-v2",
    journeyType: "catalog_creation",
    completionStep: "first_catalog",
  },
  parameters: {
    key: "parameters",
    aliases: ["use_parameters"], // Substep uses this key
    title: "Dynamic Parameters",
    description: "Add dynamic content to catalogs with parameters",
    icon: IconTypes.Tune,
    path: "/parameters",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/9269964-build-your-product-catalog-v2",
    journeyType: "discovery",
    completionStep: "use_parameters",
  },
  help: {
    key: "help",
    aliases: ["support"], // Backend support action alias
    title: "Help Center",
    description: "Get help with Catalog Machine",
    icon: IconTypes.HelpOutline,
    href: "https://help.catalogmachine.com",
    helpUrl: "https://help.catalogmachine.com",
  },
  productCatalogMapping: {
    key: "productCatalogMapping",
    title: "Product-Catalog Mapping",
    description: "View which products appear in which catalogs",
    icon: IconTypes.Dashboard,
    helpUrl:
      "https://help.catalogmachine.com/en/articles/11976355-product-catalog-mapping-v2",
    path: "/products/catalog-mapping",
    journeyType: "discovery",
    completionStep: "product-catalog-mapping",
  },
  // Order management features
  orderSettings: {
    key: "orderSettings",
    title: "Order Settings",
    description: "Configure order forms and settings",
    icon: IconTypes.Settings,
    path: "/orders/settings",
    startMode: "form", // Order settings tab name
    journeyType: "order_management",
    completionStep: "setup_orders",
  },
  orderForm: {
    key: "orderForm",
    title: "Order Form",
    description: "Create and manage order forms",
    icon: IconTypes.Order,
    path: "/orders/new",
    journeyType: "order_management",
    completionStep: "create_first_order",
  },
  orderStatus: {
    key: "orderStatus",
    title: "Order Status",
    description: "Manage order status and workflow",
    icon: IconTypes.Order,
    path: "/orders",
    journeyType: "order_management",
    completionStep: "manage_order_status",
  },
  orderExport: {
    key: "orderExport",
    title: "Export Orders",
    description: "Download order data as CSV",
    icon: IconTypes.Download,
    path: "/orders",
    journeyType: "order_management",
    completionStep: "export_orders",
  },
  paymentSettings: {
    key: "paymentSettings",
    title: "Payment Settings",
    description: "Configure Stripe payment processing",
    icon: IconTypes.Money,
    path: "/orders/settings",
    startMode: "payments", // Order settings tab name
    journeyType: "order_management",
    completionStep: "configure_payments",
  },
  // Inventory management features
  inventoryEdit: {
    key: "inventoryEdit",
    title: "Edit Inventory",
    description: "Update product quantities",
    icon: IconTypes.Edit,
    path: "/products/inventory",
    journeyType: "inventory_management",
    completionStep: "update_single_quantity",
  },
  inventoryBulk: {
    key: "inventoryBulk",
    title: "Bulk Inventory",
    description: "Update multiple quantities at once",
    icon: IconTypes.CheckAll,
    path: "/products/inventory",
    journeyType: "inventory_management",
    completionStep: "bulk_quantity_update",
  },
  inventoryFilters: {
    key: "inventoryFilters",
    title: "Inventory Filters",
    description: "Search and filter inventory",
    icon: IconTypes.Filter,
    path: "/products/inventory",
    journeyType: "inventory_management",
    completionStep: "use_inventory_filters",
  },
  inventoryExport: {
    key: "inventoryExport",
    title: "Export Inventory",
    description: "Download inventory data as CSV",
    icon: IconTypes.Download,
    path: "/products/inventory",
    journeyType: "inventory_management",
    completionStep: "export_inventory",
  },
  automation: {
    key: "automation",
    aliases: ["automation_hub"], // Backend uses snake_case
    title: "Automate with API & Zapier",
    description: "Connect to 500+ apps or build custom integrations",
    icon: IconTypes.Connect,
    path: "/automation",
    helpUrl:
      "https://help.catalogmachine.com/en/articles/3648897-automation-with-zapier",
    journeyType: "discovery",
    completionStep: "use_automation",
  },
  copilot: {
    key: "copilot",
    title: "Ask AI Assistant",
    description:
      "Open AI Copilot for help with navigation, features, and how-to questions",
    icon: IconTypes.Awesome,
    journeyType: "discovery",
  },
  support: {
    key: "support",
    title: "Contact Support",
    description: "Chat with our support team via Intercom",
    icon: IconTypes.HelpOutline,
    // No path - handled specially in useActionExecutor to open Intercom
    journeyType: "discovery",
  },
  concierge: {
    key: "concierge",
    // import_ai: AI-emitted key for "import my products via AI" — same intent as the
    // Concierge handoff. Routed through the concierge special-case in useActionExecutor
    // (fresh /concierge/new session) rather than a dead "Action not available" snackbar.
    aliases: ["import_ai"],
    title: "AI Concierge",
    description: "AI assistant for products, catalogs, and image management",
    icon: IconTypes.Concierge,
    path: "/concierge",
    journeyType: "discovery",
  },
} as const;

// Helper type to get feature type by key
export type FeatureByKey<K extends FeatureKey> = Features[K];

// Helper function to get feature with type safety
// Supports both direct keys and aliases (e.g., "showroom_products" → showroomProducts)
export function getFeature<K extends FeatureKey>(key: K): FeatureByKey<K> {
  if (!key) return null;

  // Try direct lookup first
  const directFeature = appFeatures[key];
  if (directFeature) return directFeature;

  // Search for alias match
  for (const featureKey of featureKeys) {
    const feature = appFeatures[featureKey];
    if (feature.aliases?.includes(key as string)) {
      return feature as FeatureByKey<K>;
    }
  }

  return null;
}
